import { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { createAndAddCombinedUsersData } from "../axios/axiosFunctions";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const RELATIONSHIP_LABELS = {
  manager: "Manager",
  direct: "Direct Report",
  peer: "Peer",
  otherColleague: "Other Colleague",
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CsvUploadCombined360 = ({ open, onClose, groupId, slug, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [parsedReviewees, setParsedReviewees] = useState([]);
  const [parsedReviewers, setParsedReviewers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [showAlert, setShowAlert] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [invalidRows, setInvalidRows] = useState([]);

  const downloadSampleTemplate = () => {
    const headers = [
      "type",
      "firstName",
      "lastName",
      "email",
      "revieweeEmail",
      "relationshipType",
    ];

    const sampleData = [
      ["reviewee", "John", "Doe", "john.doe@example.com", "", ""],
      ["reviewee", "Jane", "Smith", "jane.smith@example.com", "", ""],
      [
        "reviewer",
        "Alice",
        "Brown",
        "alice.brown@example.com",
        "john.doe@example.com",
        "manager",
      ],
      [
        "reviewer",
        "Bob",
        "Johnson",
        "bob.johnson@example.com",
        "john.doe@example.com",
        "peer",
      ],
      [
        "reviewer",
        "Mike",
        "Davis",
        "mike.davis@example.com",
        "jane.smith@example.com",
        "direct",
      ],
    ];

    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "360_assessment_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeRelationship = (value) => {
    if (!value) return null;
    const normalized = value.toLowerCase().trim();
    if (normalized === "manager") return "manager";
    if (normalized === "direct" || normalized === "direct report")
      return "direct";
    if (normalized === "peer") return "peer";
    if (normalized === "othercolleague" || normalized === "other colleague" || normalized === "other")
      return "otherColleague";
    return null;
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) {
      return {
        reviewees: [],
        reviewers: [],
        errors: ["CSV file must contain headers and at least one data row"],
        invalidRows: [],
      };
    }

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    // Validate headers
    const requiredHeaders = [
      "type",
      "firstname",
      "lastname",
      "email",
      "revieweeemail",
      "relationshiptype",
    ];
    const hasAllHeaders = requiredHeaders.every((header) =>
      headers.includes(header),
    );

    if (!hasAllHeaders) {
      return {
        reviewees: [],
        reviewers: [],
        errors: [
          "CSV must have all required columns: type, firstName, lastName, email, revieweeEmail, relationshipType",
        ],
        invalidRows: [],
      };
    }

    const reviewees = [];
    const reviewers = [];
    const parseErrors = [];
    const invalidRows = []; // Track invalid rows with details
    const seenRevieweeEmails = new Set();
    const seenReviewerEmails = new Set();
    const revieweeEmailMap = new Map(); // Map of reviewee emails for validation

    // First pass: collect and validate reviewees
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      const lineNum = i + 1;
      const typeValue = values[0]?.toLowerCase() || "";

      if (typeValue === "reviewee") {
        const rowData = {
          line: lineNum,
          type: "reviewee",
          data: lines[i],
          errors: [],
        };

        if (values.length < 4) {
          rowData.errors.push(
            "Missing columns (needs: type, firstName, lastName, email)",
          );
          invalidRows.push(rowData);
          parseErrors.push(`Line ${lineNum}: ${rowData.errors.join("; ")}`);
          continue;
        }

        const firstName = values[1] || "";
        const lastName = values[2] || "";
        const email = values[3] || "";

        if (!firstName) {
          rowData.errors.push("First name is required");
        }
        if (!email) {
          rowData.errors.push("Email is required");
        } else if (!validateEmail(email)) {
          rowData.errors.push("Invalid email format");
        } else {
          const emailLower = email.toLowerCase();
          if (seenRevieweeEmails.has(emailLower)) {
            rowData.errors.push(`Duplicate email: ${email}`);
          }
        }

        if (rowData.errors.length > 0) {
          invalidRows.push(rowData);
          parseErrors.push(
            `Line ${lineNum} (Reviewee): ${rowData.errors.join("; ")}`,
          );
          continue;
        }

        const emailLower = email.toLowerCase();
        seenRevieweeEmails.add(emailLower);
        revieweeEmailMap.set(emailLower, true);
        reviewees.push({
          firstName,
          lastName,
          email,
        });
      }
    }

    // Second pass: collect and validate reviewers
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      const lineNum = i + 1;
      const typeValue = values[0]?.toLowerCase() || "";

      if (typeValue === "reviewer") {
        const rowData = {
          line: lineNum,
          type: "reviewer",
          data: lines[i],
          errors: [],
        };

        if (values.length < 6) {
          rowData.errors.push(
            "Missing columns (needs: type, firstName, lastName, email, revieweeEmail, relationshipType)",
          );
          invalidRows.push(rowData);
          parseErrors.push(`Line ${lineNum}: ${rowData.errors.join("; ")}`);
          continue;
        }

        const firstName = values[1] || "";
        const lastName = values[2] || "";
        const email = values[3] || "";
        const revieweeEmail = values[4] || "";
        const relationshipTypeValue = values[5] || "";

        if (!firstName) {
          rowData.errors.push("First name is required");
        }
        if (!email) {
          rowData.errors.push("Email is required");
        } else if (!validateEmail(email)) {
          rowData.errors.push("Invalid email format");
        } else {
          const emailLower = email.toLowerCase();
          if (seenReviewerEmails.has(emailLower)) {
            rowData.errors.push(`Duplicate email: ${email}`);
          }
        }
        if (!revieweeEmail) {
          rowData.errors.push("Reviewee email is required");
        } else if (!validateEmail(revieweeEmail)) {
          rowData.errors.push("Invalid reviewee email format");
        } else if (!revieweeEmailMap.has(revieweeEmail.toLowerCase())) {
          rowData.errors.push(
            `Reviewee email "${revieweeEmail}" not found in reviewees list`,
          );
        }
        if (!relationshipTypeValue) {
          rowData.errors.push("Relationship type is required");
        } else {
          const normalizedType = normalizeRelationship(relationshipTypeValue);
          if (!normalizedType) {
            rowData.errors.push(
              `Invalid relationship type "${relationshipTypeValue}". Must be: manager, direct, peer, or otherColleague`,
            );
          }
        }

        if (rowData.errors.length > 0) {
          invalidRows.push(rowData);
          parseErrors.push(
            `Line ${lineNum} (Reviewer): ${rowData.errors.join("; ")}`,
          );
          continue;
        }

        const normalizedType = normalizeRelationship(relationshipTypeValue);
        const emailLower = email.toLowerCase();
        seenReviewerEmails.add(emailLower);
        reviewers.push({
          firstName,
          lastName,
          email,
          revieweeEmail: revieweeEmail.toLowerCase(),
          type: normalizedType,
        });
      }
    }

    return { reviewees, reviewers, errors: parseErrors, invalidRows };
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setAlertMsg("Please select a CSV file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    try {
      const text = await file.text();
      const {
        reviewees,
        reviewers,
        errors: parseErrors,
        invalidRows: invalidRowsData,
      } = parseCSV(text);

      setParsedReviewees(reviewees);
      setParsedReviewers(reviewers);
      setErrors(parseErrors);
      setInvalidRows(invalidRowsData);
      setShowPreview(true);
      setTabValue(0);
    } catch (error) {
      setAlertMsg("Error reading CSV file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (parsedReviewees.length === 0) {
      setAlertMsg("No valid reviewee data to upload");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    setLoading(true);
    try {
      const result = await createAndAddCombinedUsersData(groupId, {
        reviewees: parsedReviewees,
        reviewers: parsedReviewers,
        slug,
      });

      if (result.success) {
        setLoading(false);
        setAlertMsg(
          `Successfully added ${parsedReviewees.length} reviewee(s) and ${parsedReviewers.length} reviewer(s)`,
        );
        setAlertSeverity("success");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          handleClose();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setLoading(false);
        setAlertMsg(result.msg || "Failed to upload data");
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    } catch (error) {
      setLoading(false);
      setAlertMsg("Error uploading data");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const handleClose = () => {
    setParsedReviewees([]);
    setParsedReviewers([]);
    setErrors([]);
    setInvalidRows([]);
    setShowPreview(false);
    setShowAlert(false);
    setTabValue(0);
    onClose();
  };

  const hasValidData = parsedReviewees.length > 0 || parsedReviewers.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Upload 360 Assessment Data</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload a single CSV file containing both reviewees and their
            reviewers.
          </Typography>

          <Box display="flex" gap={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadSampleTemplate}
            >
              Download Sample Template
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Select CSV File
              <VisuallyHiddenInput
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            <strong>CSV Format:</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            For reviewees: type, firstName, lastName, email
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            For reviewers: type, firstName, lastName, email, revieweeEmail,
            relationshipType (manager/direct/peer/otherColleague)
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Validation Warnings:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {errors.length > 10 && (
                <li>...and {errors.length - 10} more errors</li>
              )}
            </ul>
          </Alert>
        )}

        {showPreview && hasValidData && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                aria-label="preview tabs"
              >
                <Tab
                  label={`Reviewees (${parsedReviewees.length})`}
                  id="tab-0"
                />
                <Tab
                  label={`Reviewers (${parsedReviewers.length})`}
                  id="tab-1"
                />
                {invalidRows.length > 0 && (
                  <Tab
                    label={`Invalid Rows (${invalidRows.length})`}
                    id="tab-2"
                    sx={{ color: "error.main" }}
                  />
                )}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {parsedReviewees.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>First Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Email</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedReviewees.map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No reviewees to display
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {parsedReviewers.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>First Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Email</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Reviewee Email</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Relationship</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedReviewers.map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.revieweeEmail}</TableCell>
                          <TableCell>
                            {RELATIONSHIP_LABELS[user.type] || user.type}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No reviewers to display
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {invalidRows.length > 0 ? (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {invalidRows.length} row(s) with errors:
                    </Typography>
                  </Alert>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#ffebee" }}>
                          <TableCell>
                            <strong>Line</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Type</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Email</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Error(s)</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invalidRows.map((row, idx) => (
                          <TableRow
                            key={idx}
                            sx={{ backgroundColor: "#fff3e0" }}
                          >
                            <TableCell>{row.line}</TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  textTransform: "capitalize",
                                  fontWeight: "bold",
                                }}
                              >
                                {row.type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {row.data.split(",")[3] || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {row.errors.map((error, errIdx) => (
                                  <Typography
                                    key={errIdx}
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    sx={{ mb: 0.5 }}
                                  >
                                    • {error}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  No invalid rows to display
                </Typography>
              )}
            </TabPanel>
          </>
        )}

        {showAlert && (
          <Alert severity={alertSeverity} sx={{ mt: 2 }}>
            {alertMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 3 }}>
          {hasValidData && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={loading || parsedReviewees.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Uploading..." : "Upload Data"}
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose} color="inherit">
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CsvUploadCombined360;
